import {extendTheme} from '@mui/joy/styles';
import {SxProps} from "@mui/joy/styles/types";
import type {PaletteRange} from '@mui/joy/styles';
import LoadingComponent from "./components/Loading/LoadingComponent";
import React from 'react';
import "@fontsource/raleway"

declare module '@mui/joy/styles' {
    interface ColorPalettePropOverrides {
        secondary: true;
    }

    interface Palette {
        secondary: PaletteRange;
    }
}


const chipStyles = (ownerState, theme): SxProps => ({
    textTransform: "capitalize",
    borderRadius: "12px",
    backgroundColor: applyTransparency(theme.palette[ownerState?.color]['400'], 0.4),
    color: theme.palette[ownerState?.color]['200'],
    backdropFilter: "blur(6px)",
    paddingLeft: 13,
    paddingRight: 13,
    paddingTop: 3,
    paddingBottom: 3,
    borderWidth: ownerState.variant === "outlined" ? 1 : 0,
    borderStyle: "solid",
    borderColor: theme.palette[ownerState?.color]['300']
})

const glassTheme = extendTheme({
    fontFamily: {
        body: "Raleway, sans-serif",
        display: "Raleway, sans-serif",
    },
    colorSchemes: {
        light: {
            palette: {
                text: {
                    icon: "#fdade5",
                    primary: "white",
                    secondary: "#fdade5",
                    tertiary: "#fcc4ea"
                },
                primary: {
                    50: "#fbd9f0",
                    100: "#fcc4ea",
                    200: "#fdade5",
                    300: "#ff95e0",
                    400: "#ff7bdc",
                    500: "#ff69d9",
                    600: "#ff2dd3",
                    700: "#f100c6",
                    800: "#da01b4",
                    900: "#c500a2",
                },
                secondary: {
                    50: '#eff6ff',
                    100: '#dbeafe',
                    200: '#bfdbfe',
                    300: '#93c5fd',
                    400: '#60a5fa',
                    500: '#3b82f6',
                    600: '#2563eb',
                    700: '#1d4ed8',
                    800: '#1e40af',
                    900: '#1e3a8a'
                }
            }
        },
        dark: {
            palette: {
                text: {
                    primary: "#ffffff",
                    secondary: "#fbd9f0",
                    tertiary: "#fcc4ea"
                },
                primary: {
                    50: "#f100c6",
                    100: "#da01b4",
                    200: "#c500a2",
                    300: "#af0090",
                    400: "#9a007e",
                    500: "#86016d",
                    600: "#72005d",
                    700: "#5e044c",
                    800: "#4a063c",
                    900: "#37062c",
                },
                secondary: {
                    50: '#d2e3ff',
                    100: '#9fc2ff',
                    200: '#6da3ff',
                    300: '#4688ff',
                    400: '#315cc4',
                    500: '#256ee6',
                    600: '#284a9c',
                    700: '#1f3470',
                    800: '#14234d',
                    900: '#0c132b'
                }
            }
        }
    },
    components: {
        JoyTextarea: {
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    backgroundColor: "rgba(0,0,0,0.2)",
                    backdropFilter: "blur(12px)",
                    color: "white",
                    borderRadius: 10,
                    borderWidth: 0.5,
                    borderColor: applyTransparency(theme.palette.primary['400'], 0.3)
                })
            }
        },
        JoyInput: {
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    backgroundColor: "rgba(0,0,0,0.2)",
                    backdropFilter: "blur(12px)",
                    color: "white",
                    borderRadius: 10,
                    borderWidth: 0.5,
                    borderColor: applyTransparency(theme.palette.primary['300'], 0.5)
                })
            }
        },
        JoyLink: {
            styleOverrides: {
                root: props => {
                    return {
                        ":hover": {
                            textDecoration: "none"
                        }
                    }
                }
            }
        },
        JoyButton: {
            defaultProps: {
                loadingIndicator: <LoadingComponent/>,
            },
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    borderRadius: 15,
                    color: ownerState.variant === "solid" ? "white" : theme.palette[ownerState.color][`${ownerState.variant}Color`],

                    ...(ownerState.variant === "outlined" && {
                        backgroundColor: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(6px)",
                        borderWidth: 1,
                        borderColor: theme.palette.neutral['500'],
                        borderStyle: "solid",
                        ":hover": {
                            borderColor: theme.palette.neutral['400'],
                            backgroundColor: "rgba(255,255,255,0.2)",
                        },
                    }),

                    ...(ownerState.variant === "soft" && {
                        backgroundColor: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(5px)",
                        ":hover": {
                            backgroundColor: "rgba(255,255,255,0.2)",
                        }
                    }),

                    ...(ownerState.variant === "plain" && {
                        backgroundColor: "transparent",
                        ":hover": {
                            backgroundColor: "rgba(0,0,0,0.3)",
                            backdropFilter: "blur(5px)"
                        }
                    }),

                    ...(ownerState.variant === "solid" && {
                        background: `linear-gradient(to right, ${theme.palette[ownerState.color]['500']}, #f100c6)`,
                        ":hover": {
                            boxShadow: ownerState['data-no-glow'] ? "none" : `0 10px 15px -3px ${theme.palette[ownerState.color]['600']}`
                        },
                        ":disabled": {
                            color: "rgba(255,255,255,0.3)"
                        },

                        ...(ownerState.loading && {
                            color: "transparent"
                        })
                    }),
                }),
            },
        },
        JoyIconButton: {
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    ...{
                        backgroundColor: applyTransparency(theme.palette[ownerState.color]['800'], 0.4),
                        backdropFilter: "blur(6px)",
                        color: theme.palette[ownerState.color]['100'],
                    },
                })
            }
        },
        JoyTypography: {
            defaultProps: {
                textAlign: "left",
            }
        },
        JoyListItem: {
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    ...(ownerState['data-selected'] && {
                        background: "linear-gradient(to right, #3b82f6, #ff2dd3)",
                        boxShadow: "0 7px 25px -2px #3b82f6",
                        color: "white",
                    }),
                })
            }
        },
        JoyChip: {
            styleOverrides: {
                //@ts-ignore
                action: ({ownerState, theme}) => ({
                    ...chipStyles(ownerState, theme),
                    ...(ownerState?.onClick && {
                        backgroundColor: ownerState['data-accent'] ? "transparent" : applyTransparency(theme.palette[ownerState?.color]['500'], 0.4),
                    }),
                    ":hover": {
                        backgroundColor: applyTransparency(theme.palette[ownerState?.color]['400'], 0.4),
                    }
                }),

                //@ts-ignore
                root: ({ownerState, theme}) => (
                    ownerState['data-accent'] ? {
                            background: "linear-gradient(to right, #3b82f6, #ff2dd3)",
                            boxShadow: "0 7px 25px -2px #3b82f6",
                            color: "white",
                            borderWidth: 0
                        } :
                        {
                            ...chipStyles(ownerState, theme),
                            ...(ownerState?.onClick && {
                                backgroundColor: 'transparent',
                                border: "none"
                            })
                        })
            }
        },
        JoyLinearProgress: {
            styleOverrides: {
                root: {
                    backdropFilter: "blur(6px)",
                    backgroundColor: "rgba(255,255,255,0.2)",
                }
            }
        },
        JoyDivider: {
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    '--Divider-lineColor': applyTransparency(theme.palette['primary'][100], 0.2)
                })
            }
        },
        JoyCard: {
            styleOverrides: {
                root: ({ownerState, theme}) => ({
                    backdropFilter: "blur(36px)",
                    borderRadius: 20,
                    borderColor: ownerState.variant === "outlined" ? `${theme.palette[ownerState.color]['600']}` : ` ${theme.palette.primary['200']}`,
                    borderWidth: 1,
                    transition: "transform 0.3s ease, border-color 0.3s ease",
                    backgroundColor: ownerState['data-light'] ? applyTransparency(theme.palette[ownerState.color][300], 0.15) : "rgba(0, 0, 0, 0.3)",
                    zIndex: 10,
                    ":hover": (!ownerState['data-glow']) ? {
                        boxShadow: (ownerState['data-border-focus'] || ownerState['data-no-glow']) ? "initial" : "0 25px 30px -12px rgba(255,108,219,0.3)",
                        borderColor: ownerState['data-border-focus'] ? `${theme.palette.primary['400']}` : `${theme.palette.neutral['600']}`,
                        transform: ownerState['data-no-animation'] ? "initial" : "scale(1.05)",
                    } : {
                        transform: ownerState['data-no-animation'] ? "initial" : "scale(1.05)",
                    },
                    ...(ownerState['data-glow'] && {
                        boxShadow: "0 7px 25px -2px #3b82f6",
                    }),
                })
            }
        },
        JoyTab: {
            styleOverrides: {
                root: {
                    color: "white",
                }
            }
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: "#163300",
                    fontSize: "large"
                }
            }
        }
    }
})

export function applyTransparency(color: string, opacity: number): string {
    // Ensure opacity is within 0-1
    const alpha = Math.min(Math.max(opacity, 0), 1);

    // HEX color
    if (color.startsWith('#')) {
        let hex = color.slice(1);
        if (hex.length === 3) {
            // e.g., #3bf → #33bbff
            hex = hex.split('').map((c) => c + c).join('');
        }
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // RGB color: rgb(r,g,b)
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
        const [, r, g, b] = rgbMatch;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    throw new Error('Unsupported color format. Use hex (#RRGGBB) or rgb(r,g,b)');
}

export const gradientPairs = [
    ["#3b82f6", "#9333ea"],     // blue → purple
    ["#10b981", "#3b82f6"],     // emerald → blue
    ["#ec4899", "#f97316"],     // pink → orange
    ["#6366f1", "#06b6d4"],     // indigo → cyan
    ["#f59e0b", "#ef4444"],     // amber → red
    ["#14b8a6", "#22c55e"],     // teal → green
    ["#8b5cf6", "#0ea5e9"],     // violet → sky
    ["#ef4444", "#f43f5e"],     // red → rose
    ["#0ea5e9", "#3b82f6"],     // sky → blue
]

export default glassTheme